import _ from 'underscore';

import { MapperHelper } from '../../../ts/mappers/MapperHelper';
import { IUserInfo } from '../../../ts/models/IUserInfo';
import {
    ICatalogUserInfo,
    IDataQualityWarningAuthorInfo,
    IDataQualityWarningInfo,
    IDownstreamDatasourcesConnectionInfo,
    IDownstreamWorkbooksConnectionInfo,
    ITagInfo
} from '../../../ts/models/resourceInfoParts';
import { IMaxDownstreamDatasources, IMaxDownstreamWorkbooks } from '../../../ts/ServerApi/types/IMaxDownstreamConnections';
import { ICatalogUserItem, IDataQualityIndicator } from '../../../ts/ServerApi/types/resourceParts';
import { IDatabase } from '../../serverApi/types/IDatabase';
import { IDatabaseInfo } from '../IDatabaseInfo';

export module DatabaseWithCrassMapper {

    const M = MapperHelper;

    export function map(
        serverApiDatabases: IDatabase[],
        maxDownstreamDatasources: IMaxDownstreamDatasources,
        maxDownstreamWorkbooks: IMaxDownstreamWorkbooks,
      ): IDatabaseInfo[] {
          return _.map(serverApiDatabases, (db: IDatabase) => {
            const dbInfo: IDatabaseInfo = {
                __typename:                         db.dbType,
                certifier:                          mapCertifier(db.certifier),
                certificationNote:                  db.certificationNote,
                controlledPermissionsEnabled:       !!db.controlledPermissionsEnabled,
                connectionType:                     db.connectionType,
                contact:                            mapContact(db.contact),
                contentType:                        'databaseWithCrass',
                createdAt:                          M.toDate(db.createdAt),
                dataQualityWarnings:                mapDataQualityWarning(db),
                dataQualityCertifications:          mapDataQualityCertification(db),
                description:                        db.description,
                downstreamWorkbooksConnection:      mapDownstreamWorkbooks(db),
                downstreamDatasourcesConnection:    mapDownstreamDatasources(db),
                maxDownstreamDatasources:           maxDownstreamDatasources ? maxDownstreamDatasources.downstreamDatasourcesConnection.totalCount : 0,
                maxDownstreamWorkbooks:             maxDownstreamWorkbooks ? maxDownstreamWorkbooks.downstreamWorkbooksConnection.totalCount : 0,
                filePath:                           db.filePath,
                hostName:                           db.hostName,
                id:                                 db.graphQLId || '',
                isCertified:                        db.isCertified,
                isEmbedded:                         db.isEmbedded,
                isGrouped:                          db.isGroupedParent,
                isGroupedParent:                    db.isGroupedParent,
                luid:                               db.luid,
                name:                               db.name,
                site:                               {id: db.siteId, luid: db.siteLuid, urlName: ''},
                tags:                               mapTags(db.tags),
                updatedAt:                          M.toDate(db.updatedAt),
                vizportalId:                        db.id
            };
            return dbInfo;
          });
        }

    function mapTags(dbTags: string[]): ITagInfo[] {
        return dbTags.map(t => {
            const tag: ITagInfo = {
                __typename: 'Tag',
                name: t
            };
            return tag;
        });
    }

    function mapDownstreamWorkbooks(db: IDatabase): IDownstreamWorkbooksConnectionInfo {
        const dsWorkbookConnection: IDownstreamWorkbooksConnectionInfo = {
            totalCount: db.downstreamWorkbookCount
        };
        return dsWorkbookConnection;
    }

    function mapDownstreamDatasources(db: IDatabase): IDownstreamDatasourcesConnectionInfo {
        const dsDatasourcesConnection: IDownstreamDatasourcesConnectionInfo = {
            totalCount: db.downstreamDataSourceCount
        };
        return dsDatasourcesConnection;
    }

    function mapDataQualityWarningOwnerToAuthor(dataQualityWarningOwner: IUserInfo | null): IDataQualityWarningAuthorInfo {
        const author: IDataQualityWarningAuthorInfo = {
            username: dataQualityWarningOwner?.username || '',
            name: dataQualityWarningOwner?.displayName || '',
            domain: dataQualityWarningOwner?.domainName || '',
            ...dataQualityWarningOwner
        };
        return author;
    }

    function mapDataQualityWarningUpdatedAt(updatedAt: string | null) {
        return updatedAt ? new Date(updatedAt) : null;
    }

    function mapDataQualityWarning(db: IDatabase): IDataQualityWarningInfo[] {
        const indicators: IDataQualityIndicator[] = db.dataQualityWarning;
        return indicators.map(dqw => {
            const dataQualityWarning: IDataQualityWarningInfo = {
                author:            mapDataQualityWarningOwnerToAuthor(dqw.owner),
                owner:             mapDataQualityWarningOwnerToAuthor(dqw.owner),
                authorDisplayName: dqw.owner?.displayName || '',
                isActive:          dqw.isActive,
                isSevere:          dqw.isSevere,
                message:           dqw.message,
                vizportalId:       dqw.id,
                warningType:       dqw.type,
                updatedAt:         mapDataQualityWarningUpdatedAt(dqw.updatedAt),
                assetUrl:          (url: string) => { return db.filePath;},
                assetName:         db.name,
                assetType:         db.dbType
            };

            return dataQualityWarning;
        });
    }

    function mapDataQualityCertification(db: IDatabase): IDataQualityWarningInfo[] {
      const indicators: IDataQualityIndicator[] = db.dataQualityCertifications;
      if(!indicators) {
        return [];
      }
      return indicators.map(dqc => {
          const dataQualityCertification: IDataQualityWarningInfo = {
              author:            mapDataQualityWarningOwnerToAuthor(dqc.owner),
              owner:             mapDataQualityWarningOwnerToAuthor(dqc.owner),
              authorDisplayName: dqc.owner?.displayName || '',
              isActive:          dqc.isActive,
              isSevere:          dqc.isSevere,
              message:           dqc.message,
              vizportalId:       dqc.id,
              warningType:       dqc.type,
              updatedAt:         mapDataQualityWarningUpdatedAt(dqc.updatedAt),
              assetUrl:          (url: string) => { return db.filePath;},
              assetName:         db.name,
              assetType:         db.dbType
          };

          return dataQualityCertification;
      });
  }

    function mapContact(contact: ICatalogUserItem): ICatalogUserInfo {
        return contact ? {
            id: contact.id,
            readOnly: contact.readOnly,
            username: contact.username,
            name: contact.displayName,
            domain: contact.domainName,
            hasServerDisplay: contact.hasServerDisplay
        } : null;
    }

    function mapCertifier(certifier: ICatalogUserItem | null): ICatalogUserInfo {
        return certifier ? {
            id: certifier.id,
            name: certifier.displayName,
            username: certifier.username,
            domain: certifier.domainName,
            readOnly: !!certifier.readOnly,
            hasServerDisplay: certifier.hasServerDisplay
        } : null;
    }
}
